import { useState, useEffect } from 'react'
import { api } from '../../services/api'
import type {
  Service,
  TenantService,
  TenantServiceWithDetails,
  TenantServiceStatus,
  ServiceCategory,
  PricingType
} from '../../services/api'

interface TenantServicesProps {
  tenantId: string
  tenantName: string
  onClose: () => void
}

const STATUS_LABELS: Record<TenantServiceStatus, string> = {
  quote: 'Cotizacion',
  pending: 'Pendiente',
  active: 'Activo',
  completed: 'Completado',
  cancelled: 'Cancelado',
  renewed: 'Renovado'
}

const STATUS_COLORS: Record<TenantServiceStatus, string> = {
  quote: 'bg-gray-500/20 text-gray-400',
  pending: 'bg-yellow-500/20 text-yellow-400',
  active: 'bg-green-500/20 text-green-400',
  completed: 'bg-blue-500/20 text-blue-400',
  cancelled: 'bg-red-500/20 text-red-400',
  renewed: 'bg-purple-500/20 text-purple-400'
}

const CATEGORY_LABELS: Record<ServiceCategory, string> = {
  package: 'Paquete',
  implementation: 'Implementacion',
  consulting: 'Consultoria',
  offensive: 'Seguridad Ofensiva',
  license: 'Licencia',
  automation: 'Automatizacion'
}

const PRICING_LABELS: Record<PricingType, string> = {
  per_user_monthly: 'Por usuario/mes',
  one_time: 'Unico',
  hourly: 'Por hora',
  project: 'Por proyecto',
  license_monthly: 'Licencia mensual',
  license_annual: 'Licencia anual'
}

export default function TenantServices({ tenantId, tenantName, onClose }: TenantServicesProps) {
  const [tenantServices, setTenantServices] = useState<TenantServiceWithDetails[]>([])
  const [availableServices, setAvailableServices] = useState<Service[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [editingService, setEditingService] = useState<TenantServiceWithDetails | null>(null)

  const fetchTenantServices = async () => {
    try {
      setLoading(true)
      setError(null)

      const [tenantResult, servicesResult] = await Promise.all([
        api.listTenantServices(tenantId),
        api.listServices({ active: true })
      ])

      if (!tenantResult.success) {
        throw new Error(tenantResult.error || 'Error al cargar servicios del cliente')
      }

      setTenantServices(tenantResult.data?.services || [])
      setAvailableServices(servicesResult.data?.services || [])
    } catch (err) {
      console.error('Error fetching tenant services:', err)
      setError(err instanceof Error ? err.message : 'Error al cargar servicios')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTenantServices()
  }, [tenantId])

  const handleAssignService = async (data: Partial<TenantService>) => {
    try {
      const result = await api.assignService({
        ...data,
        tenant_id: tenantId
      })

      if (!result.success) {
        throw new Error(result.error || 'Error al asignar servicio')
      }

      await fetchTenantServices()
      setShowAssignModal(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al asignar')
    }
  }

  const handleUpdateService = async (id: string, data: Partial<TenantService>) => {
    try {
      const result = await api.updateTenantService(id, data)

      if (!result.success) {
        throw new Error(result.error || 'Error al actualizar servicio')
      }

      await fetchTenantServices()
      setEditingService(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al actualizar')
    }
  }

  const handleRemoveService = async (id: string) => {
    if (!confirm('¿Estas seguro de eliminar este servicio del cliente?')) return

    try {
      const result = await api.removeTenantService(id)

      if (!result.success) {
        throw new Error(result.error || 'Error al eliminar servicio')
      }

      await fetchTenantServices()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al eliminar')
    }
  }

  const calculateTotal = (service: TenantServiceWithDetails) => {
    const price = service.unit_price || service.base_price
    const quantity = service.quantity || 1
    const discount = service.discount_percent || 0
    return price * quantity * (1 - discount / 100)
  }

  const totalMonthlyRevenue = tenantServices
    .filter(s => s.status === 'active' && ['per_user_monthly', 'license_monthly'].includes(s.pricing_type))
    .reduce((sum, s) => sum + calculateTotal(s), 0)

  const totalOneTimeRevenue = tenantServices
    .filter(s => s.status === 'active' && !['per_user_monthly', 'license_monthly', 'license_annual'].includes(s.pricing_type))
    .reduce((sum, s) => sum + calculateTotal(s), 0)

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-white">Servicios de {tenantName}</h2>
            <p className="text-gray-400 text-sm mt-1">
              {tenantServices.filter(s => s.status === 'active').length} servicios activos
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-6">
              {error}
              <button onClick={() => setError(null)} className="float-right">&times;</button>
            </div>
          )}

          {/* Revenue Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-800 rounded-lg p-4">
              <p className="text-gray-400 text-sm">Revenue Mensual</p>
              <p className="text-2xl font-bold text-jarix-orange">
                ${totalMonthlyRevenue.toLocaleString('en-US', { minimumFractionDigits: 0 })}
              </p>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <p className="text-gray-400 text-sm">Servicios Puntuales</p>
              <p className="text-2xl font-bold text-green-400">
                ${totalOneTimeRevenue.toLocaleString('en-US', { minimumFractionDigits: 0 })}
              </p>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <p className="text-gray-400 text-sm">Total Servicios</p>
              <p className="text-2xl font-bold text-white">{tenantServices.length}</p>
            </div>
          </div>

          {/* Add Service Button */}
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setShowAssignModal(true)}
              className="bg-jarix-orange hover:bg-jarix-pink text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Asignar Servicio
            </button>
          </div>

          {/* Services List */}
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-jarix-orange mx-auto mb-4"></div>
              <p className="text-gray-400">Cargando servicios...</p>
            </div>
          ) : tenantServices.length === 0 ? (
            <div className="text-center py-12 bg-gray-800 rounded-xl">
              <svg className="w-12 h-12 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              <p className="text-gray-400 mb-4">Este cliente no tiene servicios asignados</p>
              <button
                onClick={() => setShowAssignModal(true)}
                className="text-jarix-orange hover:underline"
              >
                Asignar primer servicio
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {tenantServices.map((ts) => (
                <div
                  key={ts.id}
                  className="bg-gray-800 rounded-lg p-4 flex items-center justify-between"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-white font-medium">{ts.service_name}</h4>
                      <span className={`px-2 py-0.5 rounded text-xs ${STATUS_COLORS[ts.status]}`}>
                        {STATUS_LABELS[ts.status]}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-400">
                      <span>{CATEGORY_LABELS[ts.category]}</span>
                      <span>{PRICING_LABELS[ts.pricing_type]}</span>
                      {ts.quantity > 1 && <span>Cantidad: {ts.quantity}</span>}
                      {ts.discount_percent > 0 && (
                        <span className="text-green-400">-{ts.discount_percent}% desc</span>
                      )}
                    </div>
                    {ts.notes && (
                      <p className="text-gray-500 text-sm mt-1">{ts.notes}</p>
                    )}
                    <div className="flex gap-4 text-xs text-gray-500 mt-2">
                      <span>Inicio: {new Date(ts.start_date).toLocaleDateString('es-CL')}</span>
                      {ts.end_date && (
                        <span>Fin: {new Date(ts.end_date).toLocaleDateString('es-CL')}</span>
                      )}
                      {ts.renewal_date && (
                        <span className="text-yellow-400">
                          Renovacion: {new Date(ts.renewal_date).toLocaleDateString('es-CL')}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="text-right mr-4">
                    <p className="text-jarix-orange font-semibold">
                      ${calculateTotal(ts).toLocaleString('en-US', { minimumFractionDigits: 0 })}
                    </p>
                    <p className="text-gray-500 text-xs">
                      {ts.unit_price ? `$${ts.unit_price} x ${ts.quantity}` : `$${ts.base_price}`}
                    </p>
                  </div>

                  <div className="flex gap-1">
                    <button
                      onClick={() => setEditingService(ts)}
                      className="text-gray-400 hover:text-jarix-orange p-2 rounded hover:bg-gray-700"
                      title="Editar"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleRemoveService(ts.id)}
                      className="text-gray-400 hover:text-red-400 p-2 rounded hover:bg-gray-700"
                      title="Eliminar"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Assign Service Modal */}
      {showAssignModal && (
        <AssignServiceModal
          services={availableServices}
          existingServiceIds={tenantServices.map(ts => ts.service_id)}
          onSave={handleAssignService}
          onClose={() => setShowAssignModal(false)}
        />
      )}

      {/* Edit Service Modal */}
      {editingService && (
        <EditTenantServiceModal
          tenantService={editingService}
          onSave={(data) => handleUpdateService(editingService.id, data)}
          onClose={() => setEditingService(null)}
        />
      )}
    </div>
  )
}

// Assign Service Modal
interface AssignServiceModalProps {
  services: Service[]
  existingServiceIds: string[]
  onSave: (data: Partial<TenantService>) => void
  onClose: () => void
}

function AssignServiceModal({ services, existingServiceIds, onSave, onClose }: AssignServiceModalProps) {
  const [selectedService, setSelectedService] = useState<Service | null>(null)
  const [formData, setFormData] = useState({
    status: 'active' as TenantServiceStatus,
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    quantity: 1,
    unit_price: 0,
    discount_percent: 0,
    license_key: '',
    renewal_date: '',
    notes: '',
    invoice_notes: ''
  })

  const availableToAssign = services.filter(s => !existingServiceIds.includes(s.id))

  const handleSelectService = (service: Service) => {
    setSelectedService(service)
    setFormData(prev => ({
      ...prev,
      unit_price: service.base_price
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedService) return

    onSave({
      service_id: selectedService.id,
      ...formData,
      end_date: formData.end_date || undefined,
      renewal_date: formData.renewal_date || undefined,
      license_key: formData.license_key || undefined,
      notes: formData.notes || undefined,
      invoice_notes: formData.invoice_notes || undefined
    })
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white">Asignar Servicio</h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Service Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Seleccionar Servicio *</label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {availableToAssign.length === 0 ? (
                <p className="text-gray-500 text-sm">No hay servicios disponibles para asignar</p>
              ) : (
                availableToAssign.map((service) => (
                  <button
                    key={service.id}
                    type="button"
                    onClick={() => handleSelectService(service)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selectedService?.id === service.id
                        ? 'border-jarix-orange bg-jarix-orange/10'
                        : 'border-gray-600 hover:border-gray-500'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-white font-medium">{service.name}</p>
                        <p className="text-gray-400 text-sm">{CATEGORY_LABELS[service.category]}</p>
                      </div>
                      <p className="text-jarix-orange font-semibold">${service.base_price}</p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {selectedService && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Estado</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as TenantServiceStatus })}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600"
                  >
                    {Object.entries(STATUS_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Cantidad</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Precio Unitario</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.unit_price}
                    onChange={(e) => setFormData({ ...formData, unit_price: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Descuento (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.discount_percent}
                    onChange={(e) => setFormData({ ...formData, discount_percent: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Fecha Inicio *</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Fecha Fin</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600"
                  />
                </div>
              </div>

              {selectedService.category === 'license' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Clave de Licencia</label>
                    <input
                      type="text"
                      value={formData.license_key}
                      onChange={(e) => setFormData({ ...formData, license_key: e.target.value })}
                      className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Fecha Renovacion</label>
                    <input
                      type="date"
                      value={formData.renewal_date}
                      onChange={(e) => setFormData({ ...formData, renewal_date: e.target.value })}
                      className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Notas</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600"
                  rows={2}
                />
              </div>

              {/* Total Preview */}
              <div className="bg-gray-700 rounded-lg p-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Subtotal:</span>
                  <span className="text-white">${(formData.unit_price * formData.quantity).toFixed(2)}</span>
                </div>
                {formData.discount_percent > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Descuento ({formData.discount_percent}%):</span>
                    <span className="text-green-400">
                      -${(formData.unit_price * formData.quantity * formData.discount_percent / 100).toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-semibold mt-2 pt-2 border-t border-gray-600">
                  <span className="text-gray-300">Total:</span>
                  <span className="text-jarix-orange">
                    ${(formData.unit_price * formData.quantity * (1 - formData.discount_percent / 100)).toFixed(2)}
                  </span>
                </div>
              </div>
            </>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!selectedService}
              className="flex-1 px-4 py-2 bg-jarix-orange text-white rounded-lg hover:bg-jarix-pink disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Asignar
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Edit Tenant Service Modal
interface EditTenantServiceModalProps {
  tenantService: TenantServiceWithDetails
  onSave: (data: Partial<TenantService>) => void
  onClose: () => void
}

function EditTenantServiceModal({ tenantService, onSave, onClose }: EditTenantServiceModalProps) {
  const [formData, setFormData] = useState({
    status: tenantService.status,
    start_date: tenantService.start_date,
    end_date: tenantService.end_date || '',
    quantity: tenantService.quantity,
    unit_price: tenantService.unit_price || tenantService.base_price,
    discount_percent: tenantService.discount_percent,
    license_key: tenantService.license_key || '',
    renewal_date: tenantService.renewal_date || '',
    notes: tenantService.notes || '',
    invoice_notes: tenantService.invoice_notes || ''
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave({
      ...formData,
      end_date: formData.end_date || undefined,
      renewal_date: formData.renewal_date || undefined,
      license_key: formData.license_key || undefined,
      notes: formData.notes || undefined,
      invoice_notes: formData.invoice_notes || undefined
    })
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white">Editar: {tenantService.service_name}</h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Estado</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as TenantServiceStatus })}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600"
              >
                {Object.entries(STATUS_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Cantidad</label>
              <input
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Precio Unitario</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.unit_price}
                onChange={(e) => setFormData({ ...formData, unit_price: parseFloat(e.target.value) || 0 })}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Descuento (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.discount_percent}
                onChange={(e) => setFormData({ ...formData, discount_percent: parseFloat(e.target.value) || 0 })}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Fecha Inicio</label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Fecha Fin</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600"
              />
            </div>
          </div>

          {tenantService.category === 'license' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Clave de Licencia</label>
                <input
                  type="text"
                  value={formData.license_key}
                  onChange={(e) => setFormData({ ...formData, license_key: e.target.value })}
                  className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Fecha Renovacion</label>
                <input
                  type="date"
                  value={formData.renewal_date}
                  onChange={(e) => setFormData({ ...formData, renewal_date: e.target.value })}
                  className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Notas</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600"
              rows={2}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Notas de Factura</label>
            <textarea
              value={formData.invoice_notes}
              onChange={(e) => setFormData({ ...formData, invoice_notes: e.target.value })}
              className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600"
              rows={2}
              placeholder="Aparecera en la factura del cliente"
            />
          </div>

          {/* Total Preview */}
          <div className="bg-gray-700 rounded-lg p-4">
            <div className="flex justify-between text-lg font-semibold">
              <span className="text-gray-300">Total:</span>
              <span className="text-jarix-orange">
                ${(formData.unit_price * formData.quantity * (1 - formData.discount_percent / 100)).toFixed(2)}
              </span>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-jarix-orange text-white rounded-lg hover:bg-jarix-pink"
            >
              Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
